import React, { useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import { supabase } from '../../utils/supabase';
import { FaInfoCircle } from 'react-icons/fa';
import './StaticPages.css';
import './Contact.css';

const Contact = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [category, setCategory] = useState('');
  const [message, setMessage] = useState('');
  const [returnContact, setReturnContact] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState('');
  const [submitSuccess, setSubmitSuccess] = useState(false);
  const [categoryError, setCategoryError] = useState('');
  const [messageError, setMessageError] = useState('');
  const [returnContactError, setReturnContactError] = useState('');
  const [showCategoryTooltip, setShowCategoryTooltip] = useState(false);

  const contactCategories = [
    { value: 'bug_report', label: 'Report a Bug' },
    { value: 'loo_issue', label: 'Report Loo Issue' },
    { value: 'database_edit', label: 'Suggest Database Edit' },
    { value: 'general', label: 'General Inquiry' }
  ];

  // Basic client-side rate limiting for submissions
  const CONTACT_RL_KEY = 'rl:contact_submissions';
  const loadRlState = () => {
    try {
      const raw = localStorage.getItem(CONTACT_RL_KEY);
      return raw ? JSON.parse(raw) : { attempts: [], cooldownUntil: 0, failures: 0 };
    } catch (_) {
      return { attempts: [], cooldownUntil: 0, failures: 0 };
    }
  };
  const saveRlState = (state) => {
    try { localStorage.setItem(CONTACT_RL_KEY, JSON.stringify(state)); } catch (_) {}
  };
  const pruneAttempts = (state, windowMs) => {
    const now = Date.now();
    state.attempts = (state.attempts || []).filter(ts => now - ts < windowMs);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    // Clear previous errors
    setCategoryError('');
    setMessageError('');
    setReturnContactError('');
    setSubmitError('');
    
    // Validate form
    if (!category) {
      setCategoryError('Please select a contact category');
      return;
    }
    
    if (!message.trim()) {
      setMessageError('Please enter a message');
      return;
    }
    
    if (message.trim().length < 10) {
      setMessageError('Message must be at least 10 characters long');
      return;
    }
    
    // Validate return contact if user is not authenticated
    if (!user && !returnContact.trim()) {
      setReturnContactError('Please provide contact information so we can get back to you');
      return;
    }
    
    if (!user && returnContact.trim().length < 5) {
      setReturnContactError('Please provide valid contact information (email or phone)');
      return;
    }
    
    // Rate-limit checks
    const minIntervalMs = 30 * 1000; // 30s between submissions
    const windowMs = 15 * 60 * 1000; // 15 minutes window
    const maxInWindow = 3; // max 3 submissions per window
    const rl = loadRlState();
    const now = Date.now();

    if (rl.cooldownUntil && now < rl.cooldownUntil) {
      const waitSec = Math.ceil((rl.cooldownUntil - now) / 1000);
      setSubmitError(`Please wait ${waitSec}s before submitting again.`);
      return;
    }
    pruneAttempts(rl, windowMs);
    if (rl.attempts.length >= maxInWindow) {
      setSubmitError('Too many submissions. Please try again later.');
      return;
    }
    if (rl.attempts.length > 0 && now - rl.attempts[rl.attempts.length - 1] < minIntervalMs) {
      const waitMs = minIntervalMs - (now - rl.attempts[rl.attempts.length - 1]);
      setSubmitError(`Please wait ${Math.ceil(waitMs / 1000)}s before submitting again.`);
      return;
    }

    // Record attempt
    rl.attempts.push(now);
    saveRlState(rl);

    setIsSubmitting(true);
    
    try {
      // Data sanitization - remove potentially dangerous characters while preserving email functionality
      // Allow: letters, numbers, spaces, common punctuation, email characters (@, ., -, _), and basic symbols
      // Remove: HTML tags, script injection, and other potentially dangerous characters
      const sanitizedCategory = category.replace(/[<>'"]*[&<>'"]/g, '');
      const sanitizedMessage = message.trim().replace(/[<>'"]*[&<>'"]/g, '');
      const sanitizedReturnContact = returnContact.trim().replace(/[<>'"]*[&<>'"]/g, '');
      
      const { error } = await supabase
        .from('contact_submissions')
        .insert([
          {
            user_email: user?.email || sanitizedReturnContact || 'anonymous@example.com',
            category: sanitizedCategory,
            message: sanitizedMessage,
            return_contact: sanitizedReturnContact || null,
            user_id: user?.id || null
          }
        ]);
      
      if (error) {
        throw error;
      }
      
      setSubmitSuccess(true);
      // Reset failures/cooldown on success
      const ok = loadRlState();
      ok.failures = 0;
      ok.cooldownUntil = 0;
      saveRlState(ok);
      setCategory('');
      setMessage('');
      setReturnContact('');
      
      // Redirect to home after 2 seconds
      setTimeout(() => {
        navigate('/');
      }, 2000);
      
    } catch (error) {
      console.error('Error submitting contact form:', error);
      setSubmitError('Failed to submit your message. Please try again.');
      // Increase backoff on failure
      const fail = loadRlState();
      fail.failures = (fail.failures || 0) + 1;
      const backoffs = [30_000, 60_000, 300_000];
      const idx = Math.min(fail.failures - 1, backoffs.length - 1);
      fail.cooldownUntil = Date.now() + backoffs[idx];
      saveRlState(fail);
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleCategoryChange = (e) => {
    setCategory(e.target.value);
    setCategoryError('');
  };

  const handleMessageChange = (e) => {
    setMessage(e.target.value);
    setMessageError('');
  };

  const handleReturnContactChange = (e) => {
    setReturnContact(e.target.value);
    setReturnContactError('');
  };

  const categoryTooltipStyle = {
    position: 'absolute',
    backgroundColor: '#fff',
    border: '2px solid #28a745',
    borderRadius: '8px',
    padding: window.innerWidth <= 768 ? '0.5rem' : '0.75rem',
    boxShadow: '0 4px 12px rgba(0,0,0,0.15)',
    zIndex: 1002,
    width: window.innerWidth <= 768 ? '250px' : '300px',
    fontSize: window.innerWidth <= 768 ? '0.8rem' : '0.85rem',
    color: '#333',
    fontWeight: '500',
    top: '50%',
    left: '100%',
    transform: 'translateY(-50%)',
    marginLeft: '0.5rem',
    opacity: showCategoryTooltip ? 1 : 0,
    visibility: showCategoryTooltip ? 'visible' : 'hidden',
    transition: 'opacity 0.3s ease, visibility 0.3s ease, transform 0.3s ease',
  };

  const categoryTooltipVisibleStyle = {
    ...categoryTooltipStyle,
    opacity: 1,
    visibility: 'visible',
    transform: 'translateY(-50%) translateX(0)',
  };

  return (
    <div className="page-root">
      <div className="content-card">
        <Link 
          to="/" 
          className="back-link"
        >
          ← Back to Home
        </Link>
        
        <h1 className="page-title">Contact Us</h1>
        <p>We'd love to hear from you! Please let us know of any inquiries or suggestions.</p>
        
        {submitSuccess && (
          <div className="contact-success">
            <h3>Thank you for your message!</h3>
            <p>We've received your submission and will get back to you soon.</p>
            <p>Redirecting you to the home page...</p>
          </div>
        )}
        
        {submitError && (
          <div className="contact-error">
            {submitError}
          </div>
        )}
        
        <form onSubmit={handleSubmit}>
          <div className="content-body">
            <div className="contact-section">
            <div style={{ position: 'relative' }}>
              <label className="contact-label">
                Contact Category
                <FaInfoCircle 
                  size={16} 
                  style={{ marginLeft: '0.5rem', color: '#28a745', cursor: 'pointer' }}
                  onMouseEnter={() => setShowCategoryTooltip(true)}
                  onMouseLeave={() => setShowCategoryTooltip(false)}
                  onTouchStart={() => setShowCategoryTooltip(true)}
                  onTouchEnd={() => setTimeout(() => setShowCategoryTooltip(false), 2000)}
                />
              </label>
              <select
                value={category}
                onChange={handleCategoryChange}
                className="contact-select"
                style={{ borderColor: categoryError ? '#ff0000' : '#ddd' }}
              >
                <option value="">Select a category...</option>
                {contactCategories.map((cat) => (
                  <option key={cat.value} value={cat.value}>
                    {cat.label}
                  </option>
                ))}
              </select>
              {categoryError && (
                <div className="contact-validation-error">
                  {window.innerWidth <= 768 ? (
                    <>
                      <div style={{
                        position: 'absolute',
                        bottom: '100%',
                        left: '50%',
                        transform: 'translateX(-50%)',
                        borderLeft: '8px solid transparent',
                        borderRight: '8px solid transparent',
                        borderTop: '8px solid #ff0000',
                      }}></div>
                      <div style={{
                        position: 'absolute',
                        bottom: '100%',
                        left: '50%',
                        transform: 'translateX(-50%)',
                        borderLeft: '7px solid transparent',
                        borderRight: '7px solid transparent',
                        borderTop: '7px solid #fff',
                        marginTop: '1px',
                      }}></div>
                    </>
                  ) : (
                    <>
                      <div style={{
                        position: 'absolute',
                        top: '50%',
                        right: '100%',
                        transform: 'translateY(-50%)',
                        borderTop: '8px solid transparent',
                        borderBottom: '8px solid transparent',
                        borderRight: '8px solid #ff0000',
                      }}></div>
                      <div style={{
                        position: 'absolute',
                        top: '50%',
                        right: '100%',
                        transform: 'translateY(-50%)',
                        borderTop: '7px solid transparent',
                        borderBottom: '7px solid transparent',
                        borderRight: '7px solid #fff',
                        marginRight: '1px',
                      }}></div>
                    </>
                  )}
                  {categoryError}
                </div>
              )}
              <div 
                style={showCategoryTooltip ? categoryTooltipVisibleStyle : categoryTooltipStyle}
              >
                {window.innerWidth <= 768 ? (
                  <>
                    <div style={{
                      position: 'absolute',
                      bottom: '100%',
                      left: '50%',
                      transform: 'translateX(-50%)',
                      borderLeft: '8px solid transparent',
                      borderRight: '8px solid transparent',
                      borderTop: '8px solid #28a745',
                    }}></div>
                    <div style={{
                      position: 'absolute',
                      bottom: '100%',
                      left: '50%',
                      transform: 'translateX(-50%)',
                      borderLeft: '7px solid transparent',
                      borderRight: '7px solid transparent',
                      borderTop: '7px solid #fff',
                      marginTop: '1px',
                    }}></div>
                  </>
                ) : (
                  <>
                    <div style={{
                      position: 'absolute',
                      top: '50%',
                      right: '100%',
                      transform: 'translateY(-50%)',
                      borderTop: '8px solid transparent',
                      borderBottom: '8px solid transparent',
                      borderRight: '8px solid #28a745',
                    }}></div>
                    <div style={{
                      position: 'absolute',
                      top: '50%',
                      right: '100%',
                      transform: 'translateY(-50%)',
                      borderTop: '7px solid transparent',
                      borderBottom: '7px solid transparent',
                      borderRight: '7px solid #fff',
                      marginRight: '1px',
                    }}></div>
                  </>
                )}
                Choose the most appropriate category for your message. This helps us route your inquiry to the right team.
              </div>
            </div>
          </div>
          
          {!user && (
            <div className="contact-section">
              <div style={{ position: 'relative' }}>
                <label className="contact-label">Return Contact Information</label>
                <input
                  type="text"
                  value={returnContact}
                  onChange={handleReturnContactChange}
                  className="contact-input"
                  placeholder="Email address or phone number..."
                  style={{ borderColor: returnContactError ? '#ff0000' : '#ddd' }}
                />
                {returnContactError && (
                  <div className="contact-validation-error">
                    {window.innerWidth <= 768 ? (
                      <>
                        <div style={{
                          position: 'absolute',
                          bottom: '100%',
                          left: '50%',
                          transform: 'translateX(-50%)',
                          borderLeft: '8px solid transparent',
                          borderRight: '8px solid transparent',
                          borderTop: '8px solid #ff0000',
                        }}></div>
                        <div style={{
                          position: 'absolute',
                          bottom: '100%',
                          left: '50%',
                          transform: 'translateX(-50%)',
                          borderLeft: '7px solid transparent',
                          borderRight: '7px solid transparent',
                          borderTop: '7px solid #fff',
                          marginTop: '1px',
                        }}></div>
                      </>
                    ) : (
                      <>
                        <div style={{
                          position: 'absolute',
                          top: '50%',
                          right: '100%',
                          transform: 'translateY(-50%)',
                          borderTop: '8px solid transparent',
                          borderBottom: '8px solid transparent',
                          borderRight: '8px solid #ff0000',
                        }}></div>
                        <div style={{
                          position: 'absolute',
                          top: '50%',
                          right: '100%',
                          transform: 'translateY(-50%)',
                          borderTop: '7px solid transparent',
                          borderBottom: '7px solid transparent',
                          borderRight: '7px solid #fff',
                          marginRight: '1px',
                        }}></div>
                      </>
                    )}
                    {returnContactError}
                  </div>
                )}
              </div>
            </div>
          )}
          
          <div className="contact-section">
            <div style={{ position: 'relative' }}>
              <label className="contact-label">Message</label>
              <textarea
                value={message}
                onChange={handleMessageChange}
                className="contact-textarea"
                placeholder="Please describe your issue or inquiry in detail..."
                rows={6}
                style={{ borderColor: messageError ? '#ff0000' : '#ddd' }}
              />
              {messageError && (
                <div className="contact-validation-error">
                  {window.innerWidth <= 768 ? (
                    <>
                      <div style={{
                        position: 'absolute',
                        bottom: '100%',
                        left: '50%',
                        transform: 'translateX(-50%)',
                        borderLeft: '8px solid transparent',
                        borderRight: '8px solid transparent',
                        borderTop: '8px solid #ff0000',
                      }}></div>
                      <div style={{
                        position: 'absolute',
                        bottom: '100%',
                        left: '50%',
                        transform: 'translateX(-50%)',
                        borderLeft: '7px solid transparent',
                        borderRight: '7px solid transparent',
                        borderTop: '7px solid #fff',
                        marginTop: '1px',
                      }}></div>
                    </>
                  ) : (
                    <>
                      <div style={{
                        position: 'absolute',
                        top: '50%',
                        right: '100%',
                        transform: 'translateY(-50%)',
                        borderTop: '8px solid transparent',
                        borderBottom: '8px solid transparent',
                        borderRight: '8px solid #ff0000',
                      }}></div>
                      <div style={{
                        position: 'absolute',
                        top: '50%',
                        right: '100%',
                        transform: 'translateY(-50%)',
                        borderTop: '7px solid transparent',
                        borderBottom: '7px solid transparent',
                        borderRight: '7px solid #fff',
                        marginRight: '1px',
                      }}></div>
                    </>
                  )}
                  {messageError}
                </div>
              )}
            </div>
          </div>
          
          <div className="contact-section">
            <div style={{ display: 'flex', gap: '1rem', flexWrap: 'wrap' }}>
              <button 
                type="submit" 
                className="contact-button"
                disabled={isSubmitting}
              >
                {isSubmitting ? 'Sending...' : 'Send Message'}
              </button>
              <button 
                type="button" 
                className="contact-cancel-button"
                onClick={() => navigate('/')}
                disabled={isSubmitting}
              >
                Cancel
              </button>
            </div>
          </div>
          </div>
        </form>
      </div>
    </div>
  );
};

export default Contact;
